// Empfänger-Autocomplete: Rang-Logik über @bitop.de-Kollegen.
//
// Herausgezogen aus der Multi-Select-`ParticipantPicker` (CalendarMs.tsx:318-333)
// als kleiner Shared-Helfer für die Freitext-Empfänger-Felder im Mail-Composer
// (P0.4) — und wiederverwendbar für „Termin aus Mail" (P2.16). Anders als der
// Kalender-Picker sperrt dies NICHT auf interne Adressaten: es liefert nur
// Vorschläge; die to/cc/bcc-Felder bleiben Freitext (Externe möglich).

export interface Colleague {
  name: string;
  email: string;
  department: string | null;
  // Zusatz-Felder für EXTERNE Teilnehmer (Salesforce-Kontakte im Kalender-
  // Picker, siehe ./participants). Alle optional → interne Kollegen aus
  // `/calendar/colleagues` und die Composer-Vorschläge bleiben unverändert.
  /** Firma/Account des externen Kontakts (SF `Account.Name`). */
  account?: string | null;
  /** > 1 = dieselbe Adresse hängt in SF an mehreren Accounts. */
  accountCount?: number;
  /** Funktion des externen Kontakts (SF `Title`). */
  title?: string | null;
  // Welche INTERNE Sorte? `person` = Kollege aus `users`, `shared` =
  // Funktionspostfach (`group_mailboxes`, Mig 0098). Kommt so aus
  // `GET /calendar/colleagues` (app/schemas/calendar.py::Colleague). Optional,
  // weil die Composer-Vorschläge und ältere Fixtures das Feld nicht führen —
  // fehlt es, ist der Eintrag eine Person.
  kind?: "person" | "shared";
}

/** Relevanz-Rang: Name-Präfix zuerst, dann Wort-Präfix (Nachname), dann
 *  Name-Substring, erst danach E-Mail/Abteilung — der gesuchte Kollege steht
 *  oben statt alphabetisch unten. Leere Query → keine Vorschläge. */
export function rankColleagues(
  colleagues: Colleague[],
  query: string,
  limit = 8
): Colleague[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [];
  const rank = (c: Colleague): number => {
    const n = c.name.toLowerCase();
    if (n.startsWith(needle)) return 0;
    if (n.split(/\s+/).some((t) => t.startsWith(needle))) return 1;
    if (n.includes(needle)) return 2;
    if (c.email.toLowerCase().includes(needle)) return 3;
    if ((c.department ?? "").toLowerCase().includes(needle)) return 4;
    return 99;
  };
  return colleagues
    .map((c) => ({ c, r: rank(c) }))
    .filter((x) => x.r < 99)
    .sort((a, b) => a.r - b.r || a.c.name.localeCompare(b.c.name))
    .slice(0, limit)
    .map((x) => x.c);
}
